'use client';

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useId,
  type ReactNode,
} from 'react';
import { Flag } from './Flag';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ComboboxCountry {
  code: string;
  name: string;
  region: string;
}

type Variant = 'inline' | 'button' | 'nav-link';
type Mode = 'select' | 'navigate';

interface CountryComboboxProps {
  countries: ComboboxCountry[];
  value: string | null;
  onChange: (code: string) => void;
  variant?: Variant;
  mode?: Mode;
  label: string;
  lang?: string;
  /** Custom trigger content — overrides default rendering */
  trigger?: ReactNode;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEARCH_THRESHOLD = 15;

// Region display order
const REGION_ORDER = [
  'Southeast Asia',
  'South Asia',
  'East Asia',
  'Central Asia',
  'Middle East',
  'Africa',
  'Europe',
  'Oceania',
  'Americas',
];

function regionSortKey(region: string): number {
  const idx = REGION_ORDER.indexOf(region);
  return idx >= 0 ? idx : REGION_ORDER.length;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByRegion(countries: ComboboxCountry[]) {
  const groups = new Map<string, ComboboxCountry[]>();
  for (const c of countries) {
    const region = c.region || 'Other';
    const list = groups.get(region);
    if (list) list.push(c);
    else groups.set(region, [c]);
  }
  // Sort by predefined order
  return [...groups.entries()].sort(
    ([a], [b]) => regionSortKey(a) - regionSortKey(b),
  );
}

function matchesSearch(country: ComboboxCountry, query: string): boolean {
  const q = query.toLowerCase();
  return (
    country.name.toLowerCase().includes(q) ||
    country.code.toLowerCase().includes(q)
  );
}

// ---------------------------------------------------------------------------
// Chevron icon
// ---------------------------------------------------------------------------

function ChevronDown({ open, size = 10 }: { open: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path d="M2.5 4 L5 6.5 L7.5 4" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Trigger variants
// ---------------------------------------------------------------------------

function InlineTrigger({
  selected,
  placeholder,
  open,
}: {
  selected: ComboboxCountry | null;
  placeholder: string;
  open: boolean;
}) {
  return (
    <span className="flex items-center gap-1">
      {selected && <Flag code={selected.code} size={16} />}
      <span>{selected?.name || placeholder}</span>
      <ChevronDown open={open} />
    </span>
  );
}

function ButtonTrigger({
  selected,
  placeholder,
  open,
}: {
  selected: ComboboxCountry | null;
  placeholder: string;
  open: boolean;
}) {
  return (
    <span className="flex items-center gap-1.5">
      {selected && <Flag code={selected.code} size={16} />}
      <span>{selected?.name || placeholder}</span>
      <ChevronDown open={open} size={12} />
    </span>
  );
}

function NavLinkTrigger({
  selected,
  placeholder,
  open,
}: {
  selected: ComboboxCountry | null;
  placeholder: string;
  open: boolean;
}) {
  return (
    <span className="flex items-center gap-1.5">
      {selected ? (
        <Flag code={selected.code} size={14} />
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2" />
        </svg>
      )}
      <span>{selected?.name || placeholder}</span>
      <ChevronDown open={open} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Option list (shared between dropdown and bottom sheet)
// ---------------------------------------------------------------------------

interface OptionListProps {
  groups: [string, ComboboxCountry[]][];
  value: string | null;
  showGroups: boolean;
  onSelect: (code: string) => void;
  activeDescendant: string | null;
  onSetActive: (id: string) => void;
  idPrefix: string;
  mode: Mode;
}

function OptionList({
  groups,
  value,
  showGroups,
  onSelect,
  activeDescendant,
  onSetActive,
  idPrefix,
  mode,
}: OptionListProps) {
  const role = mode === 'navigate' ? 'menu' : 'listbox';
  const optionRole = mode === 'navigate' ? 'menuitem' : 'option';

  return (
    <div role={role}>
      {groups.map(([region, items]) => (
        <div key={region}>
          {showGroups && (
            <div
              className="sticky top-0 z-10 bg-surface-elevated px-3 py-1 text-caption font-semibold uppercase tracking-wider text-text-dim"
              role="presentation"
            >
              {region}
            </div>
          )}
          {items.map((c) => {
            const optionId = `${idPrefix}-${c.code}`;
            const isActive = activeDescendant === optionId;
            const isSelected = c.code === value;
            return (
              <button
                key={c.code}
                id={optionId}
                role={optionRole}
                aria-selected={mode === 'select' ? isSelected : undefined}
                aria-current={mode === 'navigate' && isSelected ? 'page' : undefined}
                tabIndex={-1}
                data-code={c.code}
                onClick={() => onSelect(c.code)}
                onMouseEnter={() => onSetActive(optionId)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:outline-none ${
                  isActive ? 'bg-surface-hover' : ''
                } ${isSelected ? 'font-medium text-text-primary' : 'text-text-secondary'}`}
              >
                <Flag code={c.code} size={16} />
                <span className="truncate">{c.name}</span>
                {isSelected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="ml-auto shrink-0 text-brand"
                    aria-hidden="true"
                  >
                    <path d="M3 7.5 L5.5 10 L11 4" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CountryCombobox
// ---------------------------------------------------------------------------

export function CountryCombobox({
  countries,
  value,
  onChange,
  variant = 'inline',
  mode = 'select',
  label,
  trigger: customTrigger,
}: CountryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeDescendant, setActiveDescendant] = useState<string | null>(null);
  const uid = useId();
  const idPrefix = `cc${uid.replace(/:/g, '')}`;
  const listboxId = `${idPrefix}-list`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const showSearch = countries.length > SEARCH_THRESHOLD;
  const selected = value ? countries.find((c) => c.code === value) ?? null : null;

  // Filtered + grouped countries
  const filtered = useMemo(() => {
    if (!search) return countries;
    return countries.filter((c) => matchesSearch(c, search));
  }, [countries, search]);

  const groups = useMemo(() => groupByRegion(filtered), [filtered]);

  // Only show region headers when we have multiple groups and > threshold
  const showGroups = groups.length > 1 && countries.length > SEARCH_THRESHOLD;

  // Flat list of option IDs for keyboard nav
  const flatOptionIds = useMemo(
    () => filtered.map((c) => `${idPrefix}-${c.code}`),
    [filtered, idPrefix],
  );

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  const openPanel = useCallback(() => {
    setOpen(true);
    setSearch('');
    setActiveDescendant(null);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    setSearch('');
    setActiveDescendant(null);
    // Return focus to trigger
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const handleSelect = useCallback(
    (code: string) => {
      onChange(code);
      closePanel();
    },
    [onChange, closePanel],
  );

  // ---------------------------------------------------------------------------
  // Click outside
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        panelRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      )
        return;
      closePanel();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closePanel]);

  // ---------------------------------------------------------------------------
  // Focus management
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;
    // Auto-focus search input if shown, otherwise set first active
    requestAnimationFrame(() => {
      if (showSearch && searchRef.current) {
        searchRef.current.focus();
      }
      // Set initial active to current value or first item
      if (value) {
        const id = `${idPrefix}-${value}`;
        if (flatOptionIds.includes(id)) {
          setActiveDescendant(id);
          return;
        }
      }
      if (flatOptionIds.length > 0) {
        setActiveDescendant(flatOptionIds[0]);
      }
    });
  }, [open, showSearch, value, flatOptionIds, idPrefix]);

  // Scroll active option into view
  useEffect(() => {
    if (!activeDescendant || !open) return;
    const el = document.getElementById(activeDescendant);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeDescendant, open]);

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPanel();
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          closePanel();
          break;

        case 'ArrowDown': {
          e.preventDefault();
          const idx = activeDescendant
            ? flatOptionIds.indexOf(activeDescendant)
            : -1;
          const next = Math.min(idx + 1, flatOptionIds.length - 1);
          setActiveDescendant(flatOptionIds[next]);
          break;
        }

        case 'ArrowUp': {
          e.preventDefault();
          const idx = activeDescendant
            ? flatOptionIds.indexOf(activeDescendant)
            : 0;
          const prev = Math.max(idx - 1, 0);
          setActiveDescendant(flatOptionIds[prev]);
          break;
        }

        case 'Home':
          e.preventDefault();
          if (flatOptionIds.length > 0) setActiveDescendant(flatOptionIds[0]);
          break;

        case 'End':
          e.preventDefault();
          if (flatOptionIds.length > 0)
            setActiveDescendant(flatOptionIds[flatOptionIds.length - 1]);
          break;

        case 'Enter': {
          e.preventDefault();
          if (activeDescendant) {
            const code = activeDescendant.replace(`${idPrefix}-`, '');
            handleSelect(code);
          }
          break;
        }

        case 'Tab':
          closePanel();
          break;
      }
    },
    [
      open,
      openPanel,
      closePanel,
      activeDescendant,
      flatOptionIds,
      idPrefix,
      handleSelect,
    ],
  );

  // Type-ahead: single character while panel is open and search not focused
  const typeAheadTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const typeAheadBuffer = useRef('');

  const handleTypeAhead = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open || showSearch) return; // search input handles typing
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;

      clearTimeout(typeAheadTimer.current);
      typeAheadBuffer.current += e.key.toLowerCase();

      const match = filtered.find((c) =>
        c.name.toLowerCase().startsWith(typeAheadBuffer.current),
      );
      if (match) {
        setActiveDescendant(`${idPrefix}-${match.code}`);
      }

      typeAheadTimer.current = setTimeout(() => {
        typeAheadBuffer.current = '';
      }, 500);
    },
    [open, showSearch, filtered, idPrefix],
  );

  // ---------------------------------------------------------------------------
  // Detect mobile for bottom sheet
  // ---------------------------------------------------------------------------

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ---------------------------------------------------------------------------
  // Trigger button styles
  // ---------------------------------------------------------------------------

  const triggerClassName = (() => {
    const base =
      'cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:outline-none';
    switch (variant) {
      case 'inline':
        return `${base} inline-flex items-center gap-0.5 rounded-full border px-2.5 py-0.5 text-sm ${
          selected
            ? 'border-brand/30 bg-brand/5 font-medium text-brand hover:border-brand'
            : 'border-dashed border-text-dim/40 text-text-dim hover:border-text-secondary hover:text-text-secondary'
        } ${open ? 'border-brand bg-brand/5 text-brand' : ''}`;
      case 'button':
        return `${base} inline-flex items-center gap-1.5 rounded-lg border border-brand/40 bg-brand/5 py-1 pl-2.5 pr-2.5 text-sm font-semibold text-brand hover:border-brand hover:bg-brand/10`;
      case 'nav-link':
        return `${base} flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary`;
    }
  })();

  // ---------------------------------------------------------------------------
  // Trigger content
  // ---------------------------------------------------------------------------

  const triggerContent = customTrigger ?? (() => {
    const placeholder = label;
    switch (variant) {
      case 'inline':
        return <InlineTrigger selected={selected} placeholder={placeholder} open={open} />;
      case 'button':
        return <ButtonTrigger selected={selected} placeholder={placeholder} open={open} />;
      case 'nav-link':
        return <NavLinkTrigger selected={selected} placeholder={placeholder} open={open} />;
    }
  })();

  // ---------------------------------------------------------------------------
  // Shared panel content
  // ---------------------------------------------------------------------------

  const panelContent = (
    <>
      {showSearch && (
        <div className="sticky top-0 z-20 border-b border-border-subtle bg-surface-elevated p-2">
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setActiveDescendant(null);
            }}
            placeholder="Search..."
            aria-label="Search countries"
            className="w-full rounded-lg border border-border-subtle bg-surface-secondary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-dim focus:border-brand/50 focus:outline-none focus:ring-1 focus:ring-brand/30"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="px-3 py-4 text-center text-sm text-text-dim">
          No countries match
        </div>
      ) : (
        <OptionList
          groups={groups}
          value={value}
          showGroups={showGroups}
          onSelect={handleSelect}
          activeDescendant={activeDescendant}
          onSetActive={setActiveDescendant}
          idPrefix={idPrefix}
          mode={mode}
        />
      )}
    </>
  );

  // ---------------------------------------------------------------------------
  // Render: Mobile bottom sheet
  // ---------------------------------------------------------------------------

  if (isMobile && open) {
    return (
      <span className="relative inline-block">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => (open ? closePanel() : openPanel())}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={label}
          aria-controls={listboxId}
          className={triggerClassName}
        >
          {triggerContent}
        </button>

        {/* Backdrop */}
        <div
          className="fixed inset-0 z-50 bg-black/30 motion-reduce:transition-none"
          onClick={closePanel}
          aria-hidden="true"
        />

        {/* Bottom sheet */}
        <div
          ref={panelRef}
          id={listboxId}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          onKeyDown={(e) => {
            handleKeyDown(e);
            handleTypeAhead(e);
          }}
          className="fixed inset-x-0 bottom-0 z-50 flex max-h-[70vh] flex-col rounded-t-2xl border-t border-border-subtle bg-surface-elevated shadow-xl motion-reduce:transition-none"
        >
          {/* Drag handle */}
          <div className="flex justify-center pb-1 pt-3">
            <div className="h-1 w-10 rounded-full bg-text-dim/30" />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {panelContent}
          </div>
        </div>
      </span>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Desktop dropdown
  // ---------------------------------------------------------------------------

  return (
    <span className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? closePanel() : openPanel())}
        onKeyDown={(e) => {
          handleKeyDown(e);
          handleTypeAhead(e);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? activeDescendant ?? undefined : undefined}
        role="combobox"
        className={triggerClassName}
      >
        {triggerContent}
      </button>

      {open && (
        <>
          {/* Click-away backdrop (desktop) */}
          <div className="fixed inset-0 z-40" onClick={closePanel} aria-hidden="true" />

          <div
            ref={panelRef}
            id={listboxId}
            onKeyDown={(e) => {
              handleKeyDown(e);
              handleTypeAhead(e);
            }}
            className="absolute left-0 top-full z-50 mt-1.5 max-h-72 min-w-[220px] overflow-y-auto rounded-xl border border-border-subtle bg-surface-elevated shadow-lg"
          >
            {panelContent}
          </div>
        </>
      )}
    </span>
  );
}
